// src/oura/hooks/useOverviewData.ts
//
// What the staff Overview reads beyond the business detail: the plan, the
// client's page (plan lines, our questions, threads), the weekly notes, the
// client's requests (calls, and changes from Ask for a change) and the
// timeline. Each source loads on its own and reports its own state, so
// a slow timeline never holds up the tiles, and a failed read says so instead
// of rendering as an empty list.

import { useEffect, useState } from "react";
import { getTracker, getTrackerSummary } from "@service/aeo";
import {
    getClientPageFor,
    getTimeline,
    listCallRequests,
    listClientNotes,
    readChangeRequests,
    type CallRequest,
    type ChangeRequest,
    type ClientNote,
    type ClientPagePayload,
    type TimelinePayload,
} from "@service/aeo/clientReview";
import { getOnboarding, type Onboarding } from "@service/aeo/onboarding";
import { adaptTracker } from "../liveAudit";
import type { BusinessTracker } from "../tracker";

/**
 * One source's state. `off` is "not asked": the gate is shut, a past scan is
 * on screen, or there is no id to ask with. It is a different fact from
 * `failed`, and neither is an empty result.
 */
export type Load<T> =
    | { status: "off" }
    | { status: "loading" }
    | { status: "failed" }
    | { status: "ready"; value: T };

const OFF = { status: "off" } as const;
const LOADING = { status: "loading" } as const;
const FAILED = { status: "failed" } as const;
const ready = <T>(value: T): Load<T> => ({ status: "ready", value });

/**
 * The plan behind the latest scan, or null when none has been built.
 *
 * The summary first, always: `GET /tracker` CREATES a plan on first read, so
 * asking it blind would mint a four-week plan for every business someone
 * merely opened. Only once the summary says a plan exists is the full tracker
 * read, and then it only reads.
 */
async function loadPlan(assessmentId: string): Promise<Load<BusinessTracker | null>> {
    const summary = await getTrackerSummary(assessmentId);
    if (!summary) return FAILED;
    if (!summary.exists) return ready(null);
    const raw = await getTracker(assessmentId);
    return raw ? ready(adaptTracker(raw)) : FAILED;
}

/**
 * The client's page, or null when the business has none.
 *
 * The staff route answers for every business staff can see, so a 404 means no
 * page has been set up (nothing sold, no merchant login), which is an answer.
 * Only a read that did not get one (network, 401, 403) is a failure.
 */
async function loadClientPage(businessId: string): Promise<Load<ClientPagePayload | null>> {
    const r = await getClientPageFor(businessId);
    if (r.ok) return ready(r.payload);
    return r.reason === "notFound" ? ready(null) : FAILED;
}

const loadNotes = async (businessId: string): Promise<Load<ClientNote[]>> => ready(await listClientNotes(businessId));
const loadCalls = async (businessId: string): Promise<Load<CallRequest[]>> => ready(await listCallRequests(businessId));

/**
 * The client's change requests (Ask for a change). A route not served yet is
 * `off`, not a failure: there is nothing to read, and nothing is claimed.
 */
async function loadChanges(businessId: string): Promise<Load<ChangeRequest[]>> {
    const r = await readChangeRequests(businessId);
    if (r.ok) return ready(r.data);
    return r.reason === "notServed" ? OFF : FAILED;
}

/**
 * The business's onboarding record, or null when it has none (a 404 is the
 * answer "never onboarded", not a failure). Anything else throws and `useLoad`
 * reports it as failed, so a read that did not land never reads as "no steps".
 */
const loadOnboarding = async (businessId: string): Promise<Load<Onboarding | null>> => ready(await getOnboarding(businessId));

async function loadTimeline(businessId: string): Promise<Load<TimelinePayload>> {
    // Null is how the service reports a failed read.
    const t = await getTimeline(businessId);
    return t ? ready(t) : FAILED;
}

/**
 * Loads `loader(arg)` whenever `arg` changes; `null` asks nothing.
 *
 * The result is kept with the argument it answers, so switching business
 * reads "loading" straight away instead of showing the last business's rows
 * until the new ones land. Loaders are module functions, stable across renders.
 */
function useLoad<T>(arg: string | null, loader: (arg: string) => Promise<Load<T>>): Load<T> {
    const [settled, setSettled] = useState<{ arg: string; value: Load<T> } | null>(null);
    useEffect(() => {
        if (arg === null) return;
        let cancelled = false;
        loader(arg).then(
            (value) => {
                if (!cancelled) setSettled({ arg, value });
            },
            () => {
                if (!cancelled) setSettled({ arg, value: FAILED });
            },
        );
        return () => {
            cancelled = true;
        };
    }, [arg, loader]);
    if (arg === null) return OFF;
    return settled?.arg === arg ? settled.value : LOADING;
}

export interface OverviewData {
    plan: Load<BusinessTracker | null>;
    clientPage: Load<ClientPagePayload | null>;
    notes: Load<ClientNote[]>;
    calls: Load<CallRequest[]>;
    changes: Load<ChangeRequest[]>;
    onboarding: Load<Onboarding | null>;
    timeline: Load<TimelinePayload>;
}

export function useOverviewData({
    businessId,
    assessmentId,
    fixtureTracker,
    clientReview,
    onboarding,
    active,
}: {
    businessId: string;
    /** The latest scan, which the plan hangs off. Absent on a fixture business. */
    assessmentId?: string;
    /** A fixture business's generated plan, used in place of the live read. */
    fixtureTracker?: BusinessTracker | null;
    /**
     * Whether the client-review service may be read. It serves its local mock
     * whenever the live flag is unset, in production too, so this follows the
     * same gate as the Client section: invented notes must never reach a real
     * business's screen.
     */
    clientReview: boolean;
    /**
     * Whether the onboarding drill-in exists for this viewer. Production hides
     * it until its endpoints ship, and the Overview rows link into it, so the
     * read follows the same gate rather than pointing at a screen nobody has.
     */
    onboarding: boolean;
    /** False on a past scan: the plan, asks and notes describe today, not that scan. */
    active: boolean;
}): OverviewData {
    const clientArg = active && clientReview ? businessId : null;
    const livePlan = useLoad(active && !fixtureTracker && assessmentId ? assessmentId : null, loadPlan);
    const clientPage = useLoad(clientArg, loadClientPage);
    // The timeline exists only once something is sold: without an engagement
    // the route answers 404, which would read as a failure on every prospect.
    const sold = clientPage.status === "ready" && !!clientPage.value?.engagement;
    return {
        plan: !active ? OFF : fixtureTracker ? ready(fixtureTracker) : livePlan,
        clientPage,
        notes: useLoad(clientArg, loadNotes),
        calls: useLoad(clientArg, loadCalls),
        changes: useLoad(clientArg, loadChanges),
        // Onboarding is not part of the client-review service, so it follows
        // only the past-scan gate: its steps describe today, not that scan.
        onboarding: useLoad(active && onboarding ? businessId : null, loadOnboarding),
        timeline: useLoad(sold ? businessId : null, loadTimeline),
    };
}
